import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDevices } from '../../context/DeviceContext';
import { deriveStatus, DEVICE_STATUS, STATUS_LABEL, formatLastSeen } from '../../domain/device';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import WaterDropRoundedIcon from '@mui/icons-material/WaterDropRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import './DeviceSwitcher.css';

/** Punto de color del estado. Un solo elemento, sin iconos que compitan. */
function StatusDot({ status }) {
  return <span className={`ds-dot ds-dot--${status}`} aria-hidden="true" />;
}

/**
 * Conmutador de ambito.
 *
 * Resuelve la pregunta que el usuario se hace al llegar: "¿lo que veo es de
 * toda mi cuenta o de un aparato concreto?". Por eso el disparador muestra
 * siempre el ambito activo, no un generico "Dispositivos".
 */
export default function DeviceSwitcher() {
  const {
    devices, selectedDevice, isAccountScope, selectDevice, selectAccountScope, loading,
  } = useDevices();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);

  const close = useCallback(() => setOpen(false), []);

  // Cerrar al hacer clic fuera o con Escape: lo que cualquiera espera de un menu.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (!rootRef.current?.contains(e.target)) close(); };
    const onKey = (e) => {
      if (e.key === 'Escape') { close(); triggerRef.current?.focus(); }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  const irA = (ruta) => { close(); navigate(ruta); };

  if (loading && devices.length === 0) {
    return <div className="ds-trigger ds-trigger--skeleton" aria-hidden="true" />;
  }

  // Sin dispositivos el conmutador no tiene nada que conmutar: se convierte en
  // la accion que el usuario necesita en ese momento.
  if (devices.length === 0) {
    return (
      <button className="ds-trigger ds-trigger--empty" onClick={() => navigate('/devices/link')}>
        <AddRoundedIcon fontSize="small" />
        <span>Vincular dispositivo</span>
      </button>
    );
  }

  const etiqueta = isAccountScope ? 'Toda la cuenta' : selectedDevice?.device_name || 'Dispositivo';
  const detalle = isAccountScope
    ? `${devices.length} ${devices.length === 1 ? 'dispositivo' : 'dispositivos'}`
    : selectedDevice?.location || selectedDevice?.device_id;

  return (
    <div className="ds-root" ref={rootRef}>
      <button
        ref={triggerRef}
        className={`ds-trigger ${open ? 'is-open' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={`ds-trigger__icon ${isAccountScope ? 'is-account' : ''}`}>
          {isAccountScope ? <DashboardRoundedIcon fontSize="small" />
                          : <WaterDropRoundedIcon fontSize="small" />}
        </span>
        <span className="ds-trigger__text">
          <span className="ds-trigger__label">{etiqueta}</span>
          <span className="ds-trigger__detail">{detalle}</span>
        </span>
        {!isAccountScope && selectedDevice && (
          <StatusDot status={deriveStatus(selectedDevice)} />
        )}
        <ExpandMoreRoundedIcon className="ds-trigger__caret" fontSize="small" />
      </button>

      {open && (
        <div className="ds-menu" role="listbox">
          <button
            className={`ds-option ds-option--account ${isAccountScope ? 'is-selected' : ''}`}
            role="option"
            aria-selected={isAccountScope}
            onClick={() => { selectAccountScope(); close(); }}
          >
            <span className="ds-option__icon is-account"><DashboardRoundedIcon fontSize="small" /></span>
            <span className="ds-option__text">
              <span className="ds-option__name">Toda la cuenta</span>
              <span className="ds-option__meta">Datos combinados de tus dispositivos</span>
            </span>
            {isAccountScope && <CheckRoundedIcon className="ds-option__check" fontSize="small" />}
          </button>

          <div className="ds-separator" role="presentation">
            <span>Dispositivos</span>
          </div>

          <div className="ds-list">
            {devices.map((d, i) => {
              const status = deriveStatus(d);
              const activo = !isAccountScope && selectedDevice?.device_id === d.device_id;
              return (
                <button
                  key={d.device_id}
                  className={`ds-option ${activo ? 'is-selected' : ''}`}
                  role="option"
                  aria-selected={activo}
                  style={{ '--i': i }}
                  onClick={() => { selectDevice(d.device_id); close(); }}
                >
                  <span className="ds-option__icon"><WaterDropRoundedIcon fontSize="small" /></span>
                  <span className="ds-option__text">
                    <span className="ds-option__name">{d.device_name || d.device_id}</span>
                    <span className="ds-option__meta">
                      <StatusDot status={status} />
                      {status === DEVICE_STATUS.ONLINE
                        ? STATUS_LABEL[status]
                        : formatLastSeen(d)}
                    </span>
                  </span>
                  {activo && <CheckRoundedIcon className="ds-option__check" fontSize="small" />}
                </button>
              );
            })}
          </div>

          <div className="ds-footer">
            <button className="ds-action" onClick={() => irA('/devices')}>
              <TuneRoundedIcon fontSize="small" />
              Administrar
            </button>
            <button className="ds-action ds-action--primary" onClick={() => irA('/devices/link')}>
              <AddRoundedIcon fontSize="small" />
              Vincular
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
